/**
 * Material thumbnail renderer — module-level singleton offscreen rig.
 *
 * Renders a smooth-shaded sphere lit by a PMREM'd RoomEnvironment for one
 * material, returning a PNG data URL. Used by the Material Gallery grid to
 * show preview spheres without spawning N WebGL contexts.
 *
 * Imports from classic `three` (not `three/webgpu`) so this secondary WebGL
 * renderer can run alongside the main WebGPU viewport.
 *
 * The PBR field mapping in buildMat() mirrors the MeshPhysicalMaterial
 * wiring in viewport/createViewportMaterial.ts:228-286. If new fields are
 * added to SceneMaterial, update both sites.
 */

import {
  Color,
  IcosahedronGeometry,
  Mesh,
  MeshPhysicalMaterial,
  PerspectiveCamera,
  PMREMGenerator,
  Scene,
  type Texture,
  Vector2,
  WebGLRenderer,
} from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import type { SceneMaterial } from "../../modeling/types.ts";
import { applyDisplayColorPipeline } from "../../viewport/renderCalibration.ts";

const THUMB_SIZE = 160;

let renderer: WebGLRenderer | null = null;
let scene: Scene | null = null;
let camera: PerspectiveCamera | null = null;
let sphereMesh: Mesh | null = null;
let envTexture: Texture | null = null;

function ensureRig(): void {
  if (renderer) return;

  renderer = new WebGLRenderer({
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(1);
  renderer.setSize(THUMB_SIZE, THUMB_SIZE);
  applyDisplayColorPipeline(renderer);

  scene = new Scene();

  // Smooth-shaded sphere — Icosahedron(1, 3) = 320 triangles, low-poly
  // but dense enough for roundness. computeVertexNormals() is implicit.
  const geom = new IcosahedronGeometry(1, 3);
  sphereMesh = new Mesh(geom);
  scene.add(sphereMesh);

  camera = new PerspectiveCamera(30, 1, 0.1, 100);
  camera.position.set(0, 0, 4.5);
  camera.lookAt(0, 0, 0);

  // PMREM RoomEnvironment gives roughness/metalness meaningful reflections.
  const pmrem = new PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  envTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environment = envTexture;
  pmrem.dispose();
}

/**
 * Build a MeshPhysicalMaterial from SceneMaterial.
 *
 * Mirrors the mapping in viewport/createViewportMaterial.ts for the
 * `material` view mode, minus checker/texture/matcap/wireframe concerns.
 * Spheres always show the pure PBR shading, which is the industry
 * convention for asset-browser previews (Substance, Blender, Unity).
 */
function buildMat(p: SceneMaterial): MeshPhysicalMaterial {
  const mat = new MeshPhysicalMaterial({
    color: new Color(p.color),
    roughness: p.roughness,
    metalness: p.metalness,
    envMapIntensity: 1.0,
  });

  // Emissive
  if (p.emissiveIntensity && p.emissiveColor) {
    mat.emissive = new Color(p.emissiveColor);
    mat.emissiveIntensity = p.emissiveIntensity;
  }

  // Opacity
  const opacity = p.opacity ?? 1;
  if (opacity < 1) {
    mat.transparent = true;
    mat.opacity = opacity;
  }

  // Specular
  if (p.specularIntensity !== undefined)
    mat.specularIntensity = p.specularIntensity;

  // Normal intensity — no normal map in thumbnails, but scale affects
  // derived shading if a future texture slot is added.
  if (p.normalIntensity !== undefined && p.normalIntensity !== 1) {
    mat.normalScale = new Vector2(p.normalIntensity, p.normalIntensity);
  }

  // Coating
  if (p.clearcoat) mat.clearcoat = p.clearcoat;
  if (p.clearcoatRoughness) mat.clearcoatRoughness = p.clearcoatRoughness;

  // Sheen
  if (p.sheen) {
    mat.sheen = p.sheen;
    if (p.sheenRoughness !== undefined) mat.sheenRoughness = p.sheenRoughness;
    if (p.sheenColor) mat.sheenColor = new Color(p.sheenColor);
  }

  // Transmission (glass)
  if (p.transmission) {
    mat.transmission = p.transmission;
    mat.transparent = true;
    if (p.ior !== undefined) mat.ior = p.ior;
    if (p.thickness !== undefined) mat.thickness = p.thickness;
  }

  return mat;
}

/**
 * Render one material to a PNG data URL. Synchronous — WebGL readback on
 * a 160×160 canvas is well under a millisecond even for MeshPhysicalMaterial.
 *
 * The returned string is stable for the same SceneMaterial and can be
 * used as an `<img src>`. Callers are responsible for memoizing on the
 * material identity / hash.
 */
export function renderMaterialThumbnail(p: SceneMaterial): string {
  ensureRig();
  const mat = buildMat(p);
  const prev = sphereMesh!.material as MeshPhysicalMaterial | null;
  sphereMesh!.material = mat;
  renderer!.render(scene!, camera!);
  const url = renderer!.domElement.toDataURL("image/png");
  sphereMesh!.material = prev ?? mat;
  mat.dispose();
  return url;
}

/**
 * Render a material thumbnail as a PNG Blob, suitable for uploading to
 * R2 via the cloud library pipeline (see app/saveMaterialToLibrary.ts).
 * Returns null if the browser cannot produce a blob from the canvas.
 */
export function renderMaterialThumbnailBlob(
  p: SceneMaterial,
): Promise<Blob | null> {
  ensureRig();
  const mat = buildMat(p);
  const prev = sphereMesh!.material as MeshPhysicalMaterial | null;
  sphereMesh!.material = mat;
  renderer!.render(scene!, camera!);
  return new Promise<Blob | null>((resolve) => {
    renderer!.domElement.toBlob((blob) => {
      sphereMesh!.material = prev ?? mat;
      mat.dispose();
      resolve(blob);
    }, "image/png");
  });
}

/** Free the offscreen rig. Safe to call if never initialized. */
export function disposeMaterialThumbnailRenderer(): void {
  if (sphereMesh) {
    (sphereMesh.geometry as IcosahedronGeometry).dispose();
  }
  if (envTexture) envTexture.dispose();
  if (renderer) renderer.dispose();
  renderer = null;
  scene = null;
  camera = null;
  sphereMesh = null;
  envTexture = null;
}
